/**
 * Framework-agnostic toast store — pure state + a subscribe API, no DOM and no
 * Solid dependency, so it is fully unit-testable. `Toast.tsx` bridges it into
 * the reactive tree. Enforces a max queue length (drops the oldest) and
 * auto-expiry via an injectable scheduler.
 */
import { uuid } from '@4f/shared';

/** Semantic intent — drives color + ARIA politeness. */
export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

/**
 * An optional inline call-to-action rendered as a button in the toast (e.g. the
 * PWA "update available → Reload" prompt). Presentational only — clicking runs
 * `onClick`; the toast is not auto-dismissed, so the handler owns any follow-up.
 */
export interface ToastAction {
  /** Button label — its own accessible name (no bare literal). */
  readonly label: string;
  /** Invoked on click/activation. */
  readonly onClick: () => void;
}

/** Caller-supplied toast fields. */
export interface ToastInput {
  readonly message: string;
  readonly title?: string;
  readonly variant?: ToastVariant;
  /** Auto-dismiss after this many ms; `0` (or negative) means sticky. */
  readonly duration?: number;
  /** Optional inline action button (e.g. "Reload" for an app update). */
  readonly action?: ToastAction;
}

/** A live toast with its assigned id and resolved fields. */
export interface Toast {
  readonly id: string;
  readonly message: string;
  readonly title?: string;
  readonly variant: ToastVariant;
  readonly duration: number;
  readonly createdAt: number;
  readonly action?: ToastAction;
}

/** Schedule `fn` after `ms`; returns a canceller. Injectable for tests. */
export type Scheduler = (fn: () => void, ms: number) => () => void;

export interface ToastStoreOptions {
  /** Max toasts kept at once; adding past this drops the oldest. Default 4. */
  readonly max?: number;
  /** Default auto-dismiss ms when a toast omits `duration`. Default 5000. */
  readonly defaultDuration?: number;
  /** Timer source. Defaults to `setTimeout`/`clearTimeout`. */
  readonly schedule?: Scheduler;
}

/** Subscriber notified after every mutation. */
export type ToastListener = (toasts: readonly Toast[]) => void;

export interface ToastStore {
  /** Enqueue a toast; returns its id. */
  add(input: ToastInput): string;
  /** Remove a toast by id (idempotent). */
  dismiss(id: string): void;
  /** Remove every toast. */
  clear(): void;
  /** Current toasts, oldest first. */
  getAll(): readonly Toast[];
  /** Subscribe to changes; returns an unsubscribe fn. */
  subscribe(listener: ToastListener): () => void;
}

const defaultScheduler: Scheduler = (fn, ms) => {
  const handle = setTimeout(fn, ms);
  return () => clearTimeout(handle);
};

/** Create an isolated toast store. */
export function createToastStore(options: ToastStoreOptions = {}): ToastStore {
  const max = options.max ?? 4;
  const defaultDuration = options.defaultDuration ?? 5000;
  const schedule = options.schedule ?? defaultScheduler;

  let toasts: Toast[] = [];
  const timers = new Map<string, () => void>();
  const listeners = new Set<ToastListener>();

  const emit = (): void => {
    const snapshot = getAll();
    for (const listener of listeners) listener(snapshot);
  };

  const cancelTimer = (id: string): void => {
    const cancel = timers.get(id);
    if (cancel) {
      cancel();
      timers.delete(id);
    }
  };

  const remove = (id: string): void => {
    const before = toasts.length;
    toasts = toasts.filter((t) => t.id !== id);
    cancelTimer(id);
    if (toasts.length !== before) emit();
  };

  const getAll = (): readonly Toast[] => toasts.slice();

  const add = (input: ToastInput): string => {
    const id = uuid();
    const toast: Toast = {
      id,
      message: input.message,
      title: input.title,
      variant: input.variant ?? 'info',
      duration: input.duration ?? defaultDuration,
      createdAt: Date.now(),
      action: input.action,
    };
    toasts = [...toasts, toast];

    // Trim oldest beyond capacity.
    while (toasts.length > max) {
      const [oldest, ...rest] = toasts;
      if (!oldest) break;
      toasts = rest;
      cancelTimer(oldest.id);
    }

    if (toast.duration > 0) {
      timers.set(
        id,
        schedule(() => remove(id), toast.duration),
      );
    }
    emit();
    return id;
  };

  const clear = (): void => {
    for (const id of timers.keys()) cancelTimer(id);
    if (toasts.length === 0) return;
    toasts = [];
    emit();
  };

  const subscribe = (listener: ToastListener): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { add, dismiss: remove, clear, getAll, subscribe };
}
